/**
 * Calculators.
 *
 * The rule the surface is built around: no number appears without the clause
 * it came from, next to it, in the same eyeline. A figure that has to be
 * cross-referenced to a citation in a footer is a figure that gets quoted
 * without one.
 *
 * Three things follow in the layout.
 *
 *   · The figure list shows the citation on every row, and for the four weeks
 *     of annual leave that citation says "the National Employment Standards,
 *     incorporated by cl 22.1" rather than "cl 22.1". The distinction only
 *     matters at the moment somebody reads it aloud, which is why it is on the
 *     row and not in a tooltip.
 *   · A figure that cannot be computed keeps its place in the list with the
 *     reason where its value would be. Hiding it would read as "the agreement
 *     does not provide this"; showing it greyed reads as "this is provided and
 *     we cannot yet stand behind the number", which is the true statement.
 *   · The working is not collapsed. It is the answer to "where did you get
 *     that", and a rep who has to expand it before a meeting will not.
 */

import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  calculatorApi,
  type CalculatorResult,
  type EntitlementFigure,
  type Grounding,
} from './api.js';

export const GROUNDING_LABEL: Record<Grounding, string> = {
  stated: 'In the clause',
  nes: 'From the Act',
  ungrounded: 'Not in the clause',
};

/**
 * Paired with its glyph, so the meaning survives monochrome print.
 *
 * `△` for ungrounded rather than `!`: §7 names it "drafting defect", and a
 * figure that appears nowhere in the provision it is attributed to is exactly
 * that. An exclamation mark is not in the glyph table and reads as an alert
 * about the app rather than a mark about the document.
 */
export const GROUNDING_GLYPH: Record<Grounding, string> = {
  stated: '✓',
  nes: '§',
  ungrounded: '△',
};

/**
 * Colour marks the exception, never the ordinary case.
 *
 * `stated` was green, which put an accent on the majority of rows — a column of
 * green ticks down a list where green means nothing except "as expected", on a
 * surface whose whole design constraint is that colour belongs to the seven
 * collaborators' annotations and not to the chrome. The two states a rep has to
 * act on differently keep their hue; the ordinary one goes to ink.
 */
export const GROUNDING_COLOUR: Record<Grounding, string> = {
  stated: 'var(--color-muted)',
  nes: 'var(--color-silent)',
  ungrounded: 'var(--color-defect)',
};

const BEHAVIOUR_LABEL: Record<string, string> = {
  load_proportionate: 'Moves with your load',
  same_for_all: 'The same at any load',
  rate: 'A percentage of an amount',
  unknown: 'Not established',
};

export function Calculators({ onOpenClause }: { onOpenClause?: (number: string) => void }) {
  const figures = useQuery({ queryKey: ['calculator-figures'], queryFn: calculatorApi.figures });
  const listing = useQuery({ queryKey: ['calculator-listing'], queryFn: calculatorApi.listing });
  const [selected, setSelected] = useState<EntitlementFigure | null>(null);

  const rows = figures.data?.figures ?? [];
  const calculable = rows.filter((f) => f.calculable).length;

  return (
    <div className="flex h-full min-h-0">
      <div className="screen">
        <div className="measure-wide mx-auto">
          <header>
            {/* Sans, not serif. §4 gives the serif to the agreement's own words
                and the grotesque to the interface; a screen title set in the
                reading register reads as something the document says. */}
            <h1 className="text-[25px] font-semibold leading-[1.15] tracking-tight">
              Calculators
            </h1>
            <p className="measure mt-2 text-[13.5px] leading-relaxed text-[var(--color-muted)]">
              {rows.length > 0 ? (
                <>
                  The agreement states {rows.length} figures. {calculable} of them can be traced to
                  the provision they are attributed to and computed;{' '}
                  {rows.length - calculable} cannot, and say why.{' '}
                  {figures.data?.withoutQuantum ?? 0} further entitlements carry no number at all.
                </>
              ) : (
                'Reading the figures the agreement states…'
              )}
            </p>
          </header>

          <section className="section-rule mt-6 pt-3">
            <h2 className="label mb-1">Figures in the agreement</h2>
            <ul>
              {rows.map((figure) => (
                <FigureRow
                  key={figure.id}
                  figure={figure}
                  selected={selected?.id === figure.id}
                  onSelect={() => setSelected(figure)}
                  {...(onOpenClause ? { onOpenClause } : {})}
                />
              ))}
            </ul>
          </section>

          <ProRataPanel
            available={
              listing.data?.calculators.find((c) => c.id === 'pro-rata')?.available ?? false
            }
          />
        </div>
      </div>

      {/*
        The working panel takes `--dock-width`, the token the app already uses
        for a docked column, rather than a hand-picked `w-96`. It holds a
        three-column working table with a clause citation per row, so it is a
        dock and not a 280px rail.

        Never hidden at a breakpoint, unlike Ask's right rail: that one names
        the documents and this one is the calculator itself. Dropping it below
        1024px would leave a surface called Calculators with nothing to press.
      */}
      <aside
        className="w-[var(--dock-width)] shrink-0 overflow-y-auto border-l border-[var(--color-rule)] p-4"
        aria-label="Working"
      >
        {selected ? (
          <FigurePanel key={selected.id} figure={selected} />
        ) : (
          <>
            <p className="label mb-2">Working</p>
            <p className="text-[12.5px] leading-relaxed text-[var(--color-muted)]">
              Choose a figure to work it out at your own load. Nothing you enter here is stored — a
              0.4 load in a school of eleven teachers names a person.
            </p>
          </>
        )}
      </aside>
    </div>
  );
}

function FigureRow({
  figure,
  selected,
  onSelect,
  onOpenClause,
}: {
  figure: EntitlementFigure;
  selected: boolean;
  onSelect: () => void;
  onOpenClause?: (number: string) => void;
}) {
  return (
    <li className="row flex-col items-stretch">
      <div className="flex items-start gap-2.5">
        {/* The figure in its own gutter, as Ask sets a clause number: the eye
            finds the quantity down a column without reading a word of it, which
            is what a list of figures is for. */}
        <button
          onClick={onSelect}
          aria-current={selected}
          disabled={!figure.calculable}
          className="hit -my-1 flex min-w-0 flex-1 items-baseline gap-3 rounded-[6px] px-1.5 py-1 text-left disabled:cursor-default"
          style={{ background: selected ? 'var(--color-subtle)' : 'transparent' }}
        >
          <span
            className="w-[84px] shrink-0 text-right font-mono text-[13px] tabular-nums"
            style={{ color: figure.calculable ? 'var(--color-ink)' : 'var(--color-muted)' }}
          >
            {figure.quantum} {figure.unit}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-[13px] leading-snug">{figure.type}</span>
            <span className="label mt-0.5 block">{BEHAVIOUR_LABEL[figure.behaviour]}</span>
          </span>
        </button>

        {/*
          The citation, on the row, in a fixed gutter.

          An "nes" figure cites "the National Employment Standards, incorporated
          by cl 22.1" — twelve words, and that wording is the point: it is the
          difference between quoting the Act and being corrected in one sentence
          for quoting the clause. It sat in a `shrink-0` flex item on the same
          line as the type, so on the four-weeks-annual-leave row it would have
          taken the width the entitlement name needed and pushed the figure off.
          A fixed column lets it wrap to two lines and leaves every other row
          where it was.
        */}
        <span className="w-[188px] shrink-0 text-right">
          <span className="label block" style={{ color: GROUNDING_COLOUR[figure.grounding] }}>
            <span aria-hidden="true">{GROUNDING_GLYPH[figure.grounding]} </span>
            {GROUNDING_LABEL[figure.grounding]}
          </span>
          {onOpenClause ? (
            <button
              onClick={() => onOpenClause(figure.clause)}
              className="hit clause-number mt-0.5 block w-full text-right text-[11px] leading-snug underline decoration-[var(--color-rule-strong)] underline-offset-2"
            >
              {figure.cite}
            </button>
          ) : (
            <span className="clause-number mt-0.5 block text-[11px] leading-snug">{figure.cite}</span>
          )}
        </span>
      </div>

      {/*
        The reason, where the value would have been, and quiet.

        This was a `.callout`, which defaults to the defect hue — a red-brown
        ground and a 3px red edge on every blocked row. A third of the list
        cannot be computed, so the surface opened as a wall of alarm, and the
        thing the note actually says is "this is provided and we cannot yet
        stand behind the number". Set in the gutter under the figure it
        belongs to, muted, it says that instead.

        The 102px indent puts it under the entitlement's name: 6px of button
        inset, the 84px figure gutter, and the 12px gap inside the button.
      */}
      {figure.blockedBecause ? (
        <p className="measure mt-1.5 ml-[102px] text-[12px] leading-relaxed text-[var(--color-muted)]">
          <span aria-hidden="true">△ </span>
          {figure.blockedBecause}
        </p>
      ) : null}
    </li>
  );
}

/** One figure, at one teacher's load, with its working. */
function FigurePanel({ figure }: { figure: EntitlementFigure }) {
  const [load, setLoad] = useState('1');
  const [amount, setAmount] = useState('');

  const compute = useMutation({
    mutationFn: () =>
      calculatorApi.entitlement({
        entitlementId: figure.id,
        load: Number(load),
        ...(figure.behaviour === 'rate' && amount !== '' ? { amount: Number(amount) } : {}),
      }),
  });

  return (
    <div>
      <p className="label mb-1.5">Working</p>
      <h2 className="text-[15px] font-semibold leading-snug tracking-tight">{figure.type}</h2>
      {/* The quantum in mono beside its citation, which for an `nes` figure
          names the Act and not the clause — the one place in this panel where
          getting the authority wrong is a correction across the table. */}
      <p className="clause-number mt-1 text-[11.5px] leading-snug text-[var(--color-muted)]">
        {figure.quantum} {figure.unit} · {figure.cite}
      </p>
      {figure.because ? (
        <p className="mt-2 text-[12px] leading-relaxed text-[var(--color-muted)]">
          {figure.because}
        </p>
      ) : null}

      <form
        className="section-rule mt-4 pt-3"
        onSubmit={(event) => {
          event.preventDefault();
          compute.mutate();
        }}
      >
        <label htmlFor="figure-load" className="label block">
          Your load, as a fraction of full time
        </label>
        <input
          id="figure-load"
          type="number"
          step="0.05"
          min="0.05"
          max="1"
          value={load}
          onChange={(event) => setLoad(event.target.value)}
          className="hit mt-1 w-full rounded-[5px] border border-[var(--color-rule-strong)] px-2 py-1.5 font-mono text-[13px] tabular-nums"
        />

        {figure.behaviour === 'rate' ? (
          <>
            <label htmlFor="figure-amount" className="label mt-3 block">
              Amount the loading applies to (AUD)
            </label>
            <input
              id="figure-amount"
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              className="hit mt-1 w-full rounded-[5px] border border-[var(--color-rule-strong)] px-2 py-1.5 font-mono text-[13px] tabular-nums"
            />
          </>
        ) : null}

        {/* The one thing on this panel that acts, in the app's filled button —
            the same weight Ask gives Ask. */}
        <button
          type="submit"
          className="hit mt-3 rounded-[6px] px-3 py-1.5 text-[13px]"
          style={{ background: 'var(--color-ink)', color: 'var(--color-paper)' }}
        >
          Work it out
        </button>
      </form>

      {compute.isError ? (
        /* The refusals are the point of this product, so they are rendered as
           a finding rather than as an error toast that disappears. This one
           keeps the callout: it is a single event the person just caused, not
           a standing property of a list. */
        <p role="alert" className="callout mt-4 text-[12px] leading-relaxed">
          <span className="callout-glyph" aria-hidden="true">
            △
          </span>{' '}
          {compute.error.message}
        </p>
      ) : null}

      {compute.data ? <Working result={compute.data} /> : null}
    </div>
  );
}

/**
 * The working, always shown in full.
 *
 * Each derived step carries the clause it came from; the steps that came from
 * the person carry nothing, and the difference between those two is what the
 * column exists to show. "The workbench said so" is the answer that loses the
 * argument.
 */
function Working({ result }: { result: CalculatorResult }) {
  return (
    <div className="section-rule mt-4 pt-3">
      <p className="font-mono text-[24px] leading-none tabular-nums">
        {result.value} <span className="text-[13px] text-[var(--color-muted)]">{result.unit}</span>
      </p>

      {/*
        The arithmetic itself, beside the number it produced.

        `basis` was fetched and rendered nowhere, so the pro rata answer — the
        only figure here that is money, and the only one computed from a
        formula rather than from a multiplication — appeared with no visible
        authority for the arithmetic behind it.

        Shown only where the basis is a recovered formula. On an entitlement
        the expression is `because`, which the panel above has already set in
        prose, and the recovery sentence itself is long and is already carried
        in full by the caveat below. Printing either twice is how a working
        stops being read.
      */}
      {result.basis.recoveredFrom ? (
        <p className="clause-number mt-1.5 text-[11px] leading-snug text-[var(--color-muted)]">
          cl {result.basis.clause} · {result.basis.expression}
        </p>
      ) : null}

      <table className="mt-3 w-full text-[12px]">
        <caption className="sr-only">How this figure was worked out</caption>
        <thead>
          <tr>
            <th scope="col" className="label pb-1 text-left">
              Step
            </th>
            <th scope="col" className="label pb-1 text-right">
              Value
            </th>
            <th scope="col" className="label pb-1 text-right">
              From
            </th>
          </tr>
        </thead>
        <tbody>
          {result.working.map((step, index) => (
            <tr key={index} className="border-t border-[var(--color-rule)]">
              <td className="py-1.5 pr-2 leading-snug">{step.label}</td>
              <td className="py-1.5 text-right font-mono tabular-nums">{step.value}</td>
              {/* "you" is not a clause, and setting it in the citation register
                  makes a number the person typed look like one the document
                  states. That difference is the column's whole job. */}
              <td className="py-1.5 pl-2 text-right align-top">
                {step.from ? (
                  <span className="clause-number text-[11px]">{step.from}</span>
                ) : (
                  <span className="label">you</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {result.caveats.length > 0 ? (
        <ul className="mt-3 flex flex-col gap-2">
          {result.caveats.map((caveat) => (
            <li key={caveat} className="callout text-[12px] leading-relaxed">
              <span className="callout-glyph" aria-hidden="true">
                △
              </span>{' '}
              {caveat}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/**
 * Pro rata salary under clause 22.3(a).
 *
 * Kept on this page rather than behind a tab, because it is the calculator a
 * teacher actually arrives wanting and the only one that computes money. Our
 * copy of clause 22.3 is damaged and the formula was recovered from Award 2020
 * clause 22.5; the caveat that says so travels with every result.
 */
function ProRataPanel({ available }: { available: boolean }) {
  const [salary, setSalary] = useState('104000');
  const [attendance, setAttendance] = useState('40');
  const [nonAttendance, setNonAttendance] = useState('12');
  const [alreadyPaid, setAlreadyPaid] = useState('0');

  const compute = useMutation({
    mutationFn: () =>
      calculatorApi.proRata({
        salaryForAttendanceWeeks: Number(salary),
        attendanceWeeksInYear: Number(attendance),
        nonAttendanceWeeksInYear: Number(nonAttendance),
        salaryAlreadyPaidForNonAttendance: Number(alreadyPaid),
      }),
  });

  if (!available) return null;

  const field = (
    id: string,
    label: string,
    value: string,
    onChange: (next: string) => void,
  ): React.ReactElement => (
    <div>
      <label htmlFor={id} className="label block">
        {label}
      </label>
      <input
        id={id}
        type="number"
        min="0"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="hit mt-1 w-full rounded-[5px] border border-[var(--color-rule-strong)] px-2 py-1.5 font-mono text-[13px] tabular-nums"
      />
    </div>
  );

  return (
    <section className="section-rule mt-8 pt-3">
      <h2 className="text-[15px] font-semibold leading-snug tracking-tight">
        Pro rata salary where a load has varied
      </h2>
      <p className="clause-number mt-1 text-[11.5px] text-[var(--color-muted)]">cl 22.3(a)</p>

      <form
        className="mt-3 grid max-w-[560px] grid-cols-2 gap-x-4 gap-y-3"
        onSubmit={(event) => {
          event.preventDefault();
          compute.mutate();
        }}
      >
        {field('pro-rata-salary', 'Salary paid for attendance weeks (S)', salary, setSalary)}
        {field('pro-rata-attendance', 'Attendance weeks in the year (b)', attendance, setAttendance)}
        {field(
          'pro-rata-non-attendance',
          'Non-attendance weeks in the year (c)',
          nonAttendance,
          setNonAttendance,
        )}
        {field(
          'pro-rata-already-paid',
          'Already paid for non-attendance (d)',
          alreadyPaid,
          setAlreadyPaid,
        )}

        <div className="col-span-2">
          <button
            type="submit"
            className="hit rounded-[6px] px-3 py-1.5 text-[13px]"
            style={{ background: 'var(--color-ink)', color: 'var(--color-paper)' }}
          >
            Work it out
          </button>
        </div>
      </form>

      {compute.isError ? (
        <p role="alert" className="callout measure mt-3 text-[12px] leading-relaxed">
          <span className="callout-glyph" aria-hidden="true">
            △
          </span>{' '}
          {compute.error.message}
        </p>
      ) : null}

      {/* The result of the only calculator on this page that computes money,
          held to the width of the form it came from rather than the column. */}
      {compute.data ? (
        <div className="max-w-[560px]">
          <Working result={compute.data} />
        </div>
      ) : null}
    </section>
  );
}
